import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import AppShell from "./ui/AppShell";
import SectionHeader from "./ui/SectionHeader";
import StatusChip from "./ui/StatusChip";
import BottomNav from "./ui/BottomNav";

function ServiceCategoryPage({ service }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantityMap, setQuantityMap] = useState({});
  const [selectedGroup, setSelectedGroup] = useState("all");

  const groups = useMemo(
    () => ["all", ...Array.from(new Set(service.subServices.map((s) => s.group || "General")))],
    [service.subServices]
  );

  const filteredSubServices =
    selectedGroup === "all"
      ? service.subServices
      : service.subServices.filter((s) => (s.group || "General") === selectedGroup);

  const updateQuantity = (id, value) => {
    const parsed = Number(value);
    setQuantityMap((prev) => ({
      ...prev,
      [id]: parsed > 0 ? parsed : 1,
    }));
  };

  const getQuantity = (id) => quantityMap[id] || 1;

  const handleAddToCart = (subService) => {
    const quantity = getQuantity(subService.id);
    const finalPrice = (subService.basePrice || 0) * quantity;

    addToCart({
      type: "service",
      serviceCategory: service.title,
      title: subService.title,
      description: subService.description,
      basePrice: subService.basePrice || 0,
      finalPrice,
      quantity,
      unit: subService.unit || "job",
      priceType: subService.priceType,
      duration: subService.duration,
      warranty: subService.warranty,
      icon: subService.icon || service.icon || "🔧",
      subCategory: subService.group || "General",
      detailedRateCardKey: service.rateCardKey || "",
      workIncludes: subService.workIncludes || [],
      notFor: subService.notFor || [],
      scopeNote: subService.scopeNote || "",
    });

    alert(`${subService.title} added to cart`);
  };

  return (
    <AppShell maxWidth="1180px">
      <button onClick={() => navigate("/")} style={backBtn}>
        ← Back
      </button>

      <div style={heroCard}>
        <div style={heroTop}>
          <div style={heroMain}>
            <div style={heroIcon}>{service.icon || "🔧"}</div>
            <div>
              <h1 style={{ margin: 0 }}>{service.title}</h1>
              <p style={heroText}>{service.longDescription}</p>

              <div style={trustRow}>
                <StatusChip label="Verified professionals" tone="success" />
                <StatusChip label="Detailed pricing" tone="info" />
                <StatusChip label="Revisit support" tone="neutral" />
              </div>
            </div>
          </div>

          <div style={heroSide}>
            <div style={sideBox}>
              <div style={sideLabel}>Response promise</div>
              <div style={sideValue}>{service.responsePromise}</div>
            </div>
            <div style={sideBox}>
              <div style={sideLabel}>Inspection note</div>
              <div style={sideValue}>{service.consultationNote}</div>
            </div>
            <div style={sideBox}>
              <div style={sideLabel}>Warranty note</div>
              <div style={sideValue}>{service.warrantyPolicy}</div>
            </div>
          </div>
        </div>

        <div style={heroButtons}>
          <button onClick={() => navigate("/customer-rate-card")} style={solidBtn}>
            View detailed rate cards
          </button>
        </div>
      </div>

      <SectionHeader
        title="Choose a sub-service"
        subtitle="Select the exact type of work so pricing, worker assignment, and scope stay accurate."
        style={{ marginTop: "28px" }}
      />

      <div style={chipRow}>
        {groups.map((group) => {
          const active = selectedGroup === group;

          return (
            <button
              key={group}
              onClick={() => setSelectedGroup(group)}
              style={{
                ...chipBtn,
                background: active ? "#fff" : "#111",
                color: active ? "#000" : "#fff",
              }}
            >
              {group === "all" ? "All" : group}
            </button>
          );
        })}
      </div>

      <div style={cardsGrid}>
        {filteredSubServices.map((subService) => {
          const quantity = getQuantity(subService.id);
          const totalPrice = (subService.basePrice || 0) * quantity;
          const hasScopeDetails =
            subService.scopeNote ||
            (subService.workIncludes && subService.workIncludes.length > 0) ||
            (subService.notFor && subService.notFor.length > 0);

          return (
            <div key={subService.id} style={card}>
              <div style={cardHead}>
                <div style={subIcon}>{subService.icon || "📌"}</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: "0 0 8px 0" }}>{subService.title}</h3>
                  <p style={desc}>{subService.description}</p>
                </div>
              </div>

              <div style={metaRow}>
                <div style={metaChip}>Base: ₹{subService.basePrice}</div>
                <div style={metaChip}>{subService.duration}</div>
                <div style={metaChip}>{subService.warranty}</div>
                <div style={metaChip}>{subService.unit}</div>
              </div>

              {hasScopeDetails && (
                <div style={scopeWrap}>
                  {subService.scopeNote && (
                    <div style={scopeBanner}>
                      <div style={scopeBannerTitle}>Book the right work type</div>
                      <div style={scopeBannerText}>{subService.scopeNote}</div>
                    </div>
                  )}

                  <div style={scopeGrid}>
                    {subService.workIncludes && subService.workIncludes.length > 0 && (
                      <div style={scopeCard}>
                        <div style={scopeTitle}>What this covers</div>
                        <ul style={scopeList}>
                          {subService.workIncludes.map((item) => (
                            <li key={item} style={scopeListItem}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {subService.notFor && subService.notFor.length > 0 && (
                      <div style={scopeCardAlt}>
                        <div style={scopeTitle}>Not meant for</div>
                        <ul style={scopeList}>
                          {subService.notFor.map((item) => (
                            <li key={item} style={scopeListItem}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={explainBox}>
                <div style={explainTitle}>How pricing works</div>
                <div style={explainText}>
                  {subService.priceType === "inspection"
                    ? "Inspection visit first. Final repair or replacement charge depends on actual issue and parts."
                    : subService.priceType === "starts_at"
                    ? "This is a starting price. Final price depends on quantity, complexity, material use, and site condition."
                    : "This is a standard unit price for the defined work scope."}
                </div>
              </div>

              <div style={qtyRow}>
                <div>
                  <div style={smallLabel}>Quantity</div>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => updateQuantity(subService.id, e.target.value)}
                    style={qtyInput}
                  />
                </div>

                <div style={priceBox}>
                  <div style={smallLabel}>Estimated subtotal</div>
                  <div style={priceValue}>₹{totalPrice}</div>
                </div>
              </div>

              <div style={buttonRow}>
                <button onClick={() => navigate("/customer-rate-card")} style={outlineBtn}>
                  Rate card
                </button>
                <button onClick={() => handleAddToCart(subService)} style={solidBtnSmall}>
                  Add to Cart
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <BottomNav />
    </AppShell>
  );
}

const backBtn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  padding: "28px",
  borderRadius: "22px",
  border: "1px solid #2a2a2a",
  boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
};

const heroTop = {
  display: "grid",
  gridTemplateColumns: "1.4fr 1fr",
  gap: "18px",
};

const heroMain = {
  display: "flex",
  gap: "18px",
  alignItems: "flex-start",
};

const heroIcon = {
  fontSize: "54px",
  lineHeight: 1,
};

const heroText = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  marginTop: "10px",
  marginBottom: "12px",
};

const trustRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "12px",
};

const heroSide = {
  display: "grid",
  gap: "12px",
};

const sideBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "14px",
  padding: "14px",
};

const sideLabel = {
  color: "#9f9f9f",
  fontSize: "13px",
  marginBottom: "6px",
};

const sideValue = {
  color: "#fff",
  lineHeight: "1.6",
};

const heroButtons = {
  marginTop: "18px",
};

const chipRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "18px",
};

const chipBtn = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  cursor: "pointer",
};

const cardsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
  gap: "18px",
};

const card = {
  background: "linear-gradient(180deg, #171717, #111)",
  border: "1px solid #262626",
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
};

const cardHead = {
  display: "flex",
  gap: "14px",
  alignItems: "flex-start",
};

const subIcon = {
  fontSize: "28px",
  lineHeight: 1,
};

const desc = {
  color: "#cfcfcf",
  lineHeight: "1.7",
  margin: 0,
};

const metaRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  marginTop: "16px",
};

const metaChip = {
  padding: "8px 12px",
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "999px",
  fontSize: "13px",
  color: "#fff",
};

const scopeWrap = {
  marginTop: "16px",
  display: "grid",
  gap: "12px",
};

const scopeBanner = {
  padding: "14px",
  background: "#1b1205",
  border: "1px solid #473112",
  borderRadius: "12px",
};

const scopeBannerTitle = {
  fontWeight: "bold",
  color: "#ffd488",
  marginBottom: "6px",
};

const scopeBannerText = {
  color: "#f0dcc0",
  lineHeight: "1.7",
  fontSize: "14px",
};

const scopeGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const scopeCard = {
  padding: "14px",
  background: "#0d1710",
  border: "1px solid #24432c",
  borderRadius: "12px",
};

const scopeCardAlt = {
  padding: "14px",
  background: "#1b1010",
  border: "1px solid #4b2323",
  borderRadius: "12px",
};

const scopeTitle = {
  fontWeight: "bold",
  marginBottom: "8px",
};

const scopeList = {
  margin: 0,
  paddingLeft: "18px",
  color: "#d1d1d1",
};

const scopeListItem = {
  marginBottom: "8px",
  lineHeight: "1.6",
};

const explainBox = {
  marginTop: "16px",
  padding: "14px",
  background: "#0d1621",
  border: "1px solid #1f3047",
  borderRadius: "12px",
};

const explainTitle = {
  fontWeight: "bold",
  marginBottom: "6px",
};

const explainText = {
  color: "#d1d1d1",
  lineHeight: "1.7",
  fontSize: "14px",
};

const qtyRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  flexWrap: "wrap",
  marginTop: "18px",
  alignItems: "flex-end",
};

const smallLabel = {
  color: "#9f9f9f",
  fontSize: "13px",
  marginBottom: "6px",
};

const qtyInput = {
  width: "110px",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
};

const priceBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "12px 14px",
  minWidth: "150px",
};

const priceValue = {
  fontWeight: "bold",
  fontSize: "24px",
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "18px",
};

const solidBtn = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const solidBtnSmall = {
  flex: 1,
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const outlineBtn = {
  flex: 1,
  padding: "12px 18px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

export default ServiceCategoryPage;
